import { differenceInDays } from 'date-fns'
import { pick } from 'lodash-es'
import type { Filter, FindOptions, UpdateFilter, UpdateOptions } from 'mongodb'
import { v4 as clientUuid } from 'uuid'
import type { MapFiltersType } from '~/atoms'
import {
  RATE_TYPES,
  SF_IN_ACRES,
  STATUSES,
  TYPES,
  fmtName,
  fmtNumberAbbr,
} from '~/utils'
import type { ModelCompany } from './company'
import { DataLayerEntry, DataLayerEntryField } from './dataLayer'
import {
  ListingTransaction,
  META_FIELDS,
  type Listing,
  type ListingBroker,
  type ListingLeaseSpace,
  type MetaFieldConfig,
} from './listing'
import type { Organization } from './organization'
import type { ModelUser, TokenUser, User } from './user'

type CSVRow = Record<string, string | undefined>

type MetaLabelValue = {
  key: string
  label: string
  value: string
}

type LeaseMetaValue = {
  key: string
  label: string
  value: string
}

export enum ModelStatus {
  active = 'active',
  inactive = 'inactive',
  suspended = 'suspended',
}

export type ModelFind =
  | {
      [key: string]: any
      'organization._id': string | any
    }
  | {
      [key: string]: any
      'organization.slug': string | any
    }
  | {
      [key: string]: any
      $or: (
        | { 'organization.slug': string | { $in: string[] } }
        | { externalMembershipSlugs: { $in: string[] } }
      )[]
    }

export type ModelFindOptions<T> = FindOptions & {
  filter: Filter<T> &
    (
      | { 'organization._id': string }
      | { 'organization.slug': string }
      | {
          organization: {
            _id: string
          }
        }
      | {
          organization: {
            slug: string
          }
        }
      | {
          $or: (
            | { 'organization.slug': string | { $in: string[] } }
            | { externalMembershipSlugs: { $in: string[] } }
          )[]
        }
    )
}

export type ModelUpdateOptions<T> = {
  filter: Filter<T>
  update: UpdateFilter<T> | Document[]
  options?: UpdateOptions
}

export type ExternalLink = {
  name: string
  url: string
  type?: string
}

export type Image = {
  _id: string
  url: string
  thumbUrl: string
}

export function getDefaultValues<T = Record<string, any>>(
  user?: User | ModelUser | TokenUser | null,
  defaultValues?: { [key: string]: any }
) {
  const _id = clientUuid()
  return {
    _id,
    tid: _id.split('-')[0],
    created: new Date().toISOString(),
    modified: new Date().toISOString(),
    user: user ? getModelUser(user) : undefined,
    ...defaultValues,
  } as T
}

export function getOrganizationSlugFilter(organization: Organization) {
  return organization.options.searchOrganizationSlugs &&
    organization.options.searchOrganizationSlugs.length > 0
    ? {
        $in: [
          organization.slug,
          ...organization.options.searchOrganizationSlugs,
        ],
      }
    : organization.slug
}

export function getModelUser(user: any): ModelUser {
  const modelUser = pick(user, [
    '_id',
    'tid',
    'slug',
    'organization',
    'firstName',
    'lastName',
    'email',
    'phone',
    'group',
    'role',
    'company',
  ])
  if (modelUser.company) {
    modelUser.company = pick(modelUser.company, [
      '_id',
      'tid',
      'slug',
      'name',
      'childCompanyIds',
    ])
  }
  if (!modelUser.role) {
    delete modelUser.role
  }
  return modelUser
}

export function camelCaseToWords(s: string) {
  const result = s.replace(/([A-Z])/g, ' $1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const PHONE_REGEX = new RegExp(
  /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
)
export const EMAIL_REGEX = new RegExp(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
)
export const WEB_REGEX = new RegExp(
  /^(http(s?):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,8}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/
)

export function regexPhone(val: string | undefined) {
  if (val && !val.match(PHONE_REGEX)) {
    return false
  }
  return true
}

export function regexWeb(val: string | undefined) {
  if (val && !val.match(WEB_REGEX)) {
    return false
  }
  return true
}

export function regexEmail(val: string | undefined) {
  if (val && !val.match(EMAIL_REGEX)) {
    return false
  }
  return true
}

export const getListingTypeLabel = (listing: Listing) => {
  const labels: { [key: string]: string } = {
    sale: 'For Sale',
    lease: 'For Lease',
    saleLease: 'For Sale/Lease',
  }
  if (listing.status === 'leased') {
    return 'Fully Leased'
  } else if (listing.status === 'sold') {
    return 'Sold'
  }
  return labels[listing.type]
}

export function getDom(date: string | Date) {
  const hours = (new Date().getTime() - new Date(date).getTime()) / 1000 / 3600
  const days = Math.round(hours / 24)
  return days
}

export function getSaleMetaValues(listing: Listing) {
  if (listing.type === 'sale' || listing.type === 'saleLease') {
    const values = []
    // const keys = ['price', 'buildingSf', 'acres']
    if (listing.meta.price) {
      values.push({
        label: 'Price',
        value: '$',
      })
    }
  }
  return []
}

export function getLeaseMetaValues(
  listing: Listing,
  abbr = false,
  valuesOnly = false
): LeaseMetaValue[] {
  if (
    (listing.type === 'lease' || listing.type === 'saleLease') &&
    (listing.leaseSpaces || []).length > 0 &&
    listing.filters
  ) {
    const filters = listing.filters!
    const values: LeaseMetaValue[] = []

    if (abbr && filters.rateLabelAbbr) {
      values.push({
        key: 'leaseRate',
        label: listing.status === 'leased' ? 'Listed Rate' : 'Lease Rate',
        value: filters.rateLabelAbbr,
      })
    } else if (filters.rateLabel) {
      values.push({
        key: 'leaseRate',
        label: listing.status === 'leased' ? 'Listed Rate' : 'Lease Rate',
        value: filters.rateLabel,
      })
    } else {
      const minRate =
        listing.leaseSpaces!.length === 1
          ? listing.leaseSpaces![0].minRate
          : listing.status === 'leased'
            ? filters.minRateHistoric
            : filters.minRate
      const maxRate =
        listing.leaseSpaces!.length === 1
          ? listing.leaseSpaces![0].maxRate
          : listing.status === 'leased'
            ? filters.maxRateHistoric
            : filters.maxRate
      const rateType =
        listing.leaseSpaces!.length === 1
          ? RATE_TYPES[listing.leaseSpaces![0].rateType!]
          : 'SF/year'

      if (
        (!minRate && !maxRate) ||
        listing.leaseSpaces?.some((space) => space.rateNegotiable)
      ) {
        values.push({
          key: 'leaseRate',
          label: listing.status === 'leased' ? 'Listed Rate' : 'Lease Rate',
          value: 'Negotiable',
        })
        // values['Lease Rate'] = `$${minRate} - $${maxRate} ${rateType}`
      } else if (minRate && maxRate && minRate !== maxRate) {
        values.push({
          key: 'leaseRate',
          label: listing.status === 'leased' ? 'Listed Rate' : 'Lease Rate',
          value: valuesOnly
            ? `${minRate} - ${maxRate}`
            : abbr
              ? `$${fmtNumberAbbr(minRate)} - $${fmtNumberAbbr(maxRate)} ${rateType}`
              : `$${minRate.toLocaleString()} - $${maxRate.toLocaleString()} ${rateType}`,
        })
      } else if (minRate) {
        values.push({
          key: 'leaseRate',
          label: listing.status === 'leased' ? 'Listed Rate' : 'Lease Rate',
          value: valuesOnly
            ? minRate.toString()
            : abbr
              ? `$${fmtNumberAbbr(minRate)} ${rateType}`
              : `$${minRate.toLocaleString()} ${rateType}`,
        })
      }
    }

    const minSf =
      listing.status === 'leased' ? filters.minSfHistoric : filters.minSf
    const maxSf =
      listing.status === 'leased' ? filters.maxSfHistoric : filters.maxSf
    if (minSf && maxSf && minSf !== maxSf) {
      values.push({
        key: 'availableSf',
        label: listing.status === 'leased' ? 'Listed SF' : 'Available SF',
        value: valuesOnly
          ? `${minSf} - ${maxSf}`
          : abbr
            ? `${fmtNumberAbbr(minSf)} - ${fmtNumberAbbr(maxSf)} SF`
            : `${minSf.toLocaleString()} - ${maxSf.toLocaleString()} SF`,
      })
    } else if (minSf) {
      values.push({
        key: 'availableSf',
        label: listing.status === 'leased' ? 'Listed SF' : 'Available SF',
        value: valuesOnly
          ? minSf.toString()
          : abbr
            ? `${fmtNumberAbbr(minSf)} SF`
            : `${minSf.toLocaleString()} SF`,
      })
    }

    if (
      listing.leaseSpaces &&
      listing.leaseSpaces.length > 1 &&
      (listing.status === 'active' || listing.status === 'draft') &&
      listing.options?.hideTotalAvailableSf !== true
    ) {
      let total = listing.leaseSpaces
        .filter((space) => space.available)
        .reduce((total, space) => {
          const spaceSf = space.maxAvailableSf || space.minAvailableSf || 0
          return total + spaceSf
        }, 0)

      if (listing.meta.buildingSf && total > listing.meta.buildingSf) {
        total = listing.meta.buildingSf
      }

      if (total > 0 && total !== minSf) {
        values.push({
          key: 'totalAvailableSf',
          label: 'Total Available SF',
          value: valuesOnly
            ? total.toLocaleString()
            : abbr
              ? `${fmtNumberAbbr(total)} SF`
              : `${total.toLocaleString()} SF`,
        })
      }
    }

    return values
  }
  return []
}

export function getMetaValue(
  value: any,
  metaConfig: MetaFieldConfig,
  meta: Record<string, string>,
  valuesOnly = false,
  propertyTypes: string[]
): string {
  if (valuesOnly) {
    return typeof metaConfig.format === 'function'
      ? (metaConfig.format({ value, meta, valuesOnly, propertyTypes }) ?? '')
      : value
  }
  if (metaConfig.format === 'currency') {
    value = currencyFormatter.format(value).replace('.00', '')
  } else if (typeof metaConfig.format === 'function') {
    value = metaConfig.format({ value, meta, valuesOnly, propertyTypes })
  } else if (value === true) {
    return 'Yes'
  } else if (
    metaConfig.format === 'number' ||
    metaConfig.suffix ||
    metaConfig.prefix
  ) {
    // Assuming anything with prefix/suffix will be a number, may have to change
    value = value.toLocaleString()
  }
  if (metaConfig.prefix) {
    value = `${metaConfig.prefix} ${value}`
  }
  if (metaConfig.suffix) {
    value =
      metaConfig.suffix === '%' ? `${value}%` : `${value} ${metaConfig.suffix}`
  }
  return value
}

export function getMetaLabelValues(
  listing: Listing,
  valuesOnly = false
): MetaLabelValue[] {
  const meta = listing.meta as { [key: string]: any }
  return Object.keys(META_FIELDS)
    .filter(
      (key) =>
        META_FIELDS[key].render !== false &&
        (!!meta[key] || META_FIELDS[key].generated)
    )
    .map((key) => {
      const metaConfig = META_FIELDS[key]
      const value = getMetaValue(
        meta[key] || '',
        metaConfig,
        meta,
        valuesOnly,
        listing.propertyTypes
      )
      const label = metaConfig.labelFormat
        ? metaConfig.labelFormat({ status: listing.status })
        : (metaConfig.label ?? camelCaseToWords(key))
      return {
        key,
        label,
        value,
      }
    })
    .filter(({ value }) => value)
}

export function getReportMetaLabelValues(listing: Listing) {
  const leaseMeta = getLeaseMetaValues(listing)
  const availableSpaces = (listing.leaseSpaces || []).filter((s) => s.available)
  const availableSpacesEntry =
    availableSpaces.length > 0
      ? [
          {
            key: 'availableSpaces',
            label: 'Available Spaces',
            value: availableSpaces.length.toString(),
          },
        ]
      : []
  const transactionMetaLabelValues = []

  if (
    listing.status === 'sold' &&
    listing.transactions &&
    listing.transactions.length > 0
  ) {
    const soldPrice = listing.transactions[0].sale?.price
    if (listing.transactions[0].confidential) {
      transactionMetaLabelValues.push({
        key: 'soldPrice',
        label: 'Sold Price',
        value: 'Confidential',
      })
    } else if (soldPrice) {
      transactionMetaLabelValues.push({
        key: 'soldPrice',
        label: 'Sold Price',
        value: `$${soldPrice.toLocaleString()}`,
      })
    }
    const created = new Date(listing.activeDate || listing.created)
    const dom = differenceInDays(
      new Date(listing.transactions[0].date),
      created
    )

    transactionMetaLabelValues.push({
      key: 'soldDate',
      label: 'Sold Date',
      value: listing.transactions[0].date.split('T')[0],
    })
    if (dom > 10) {
      transactionMetaLabelValues.push({
        key: 'daysOnMarket',
        label: 'Days on Market',
        value: dom.toString(),
      })
    }
  }

  const metaLabelValues = [
    ...transactionMetaLabelValues,
    ...(leaseMeta.length > 0 ? leaseMeta : []),
    ...availableSpacesEntry,
    ...getMetaLabelValues(listing),
    {
      key: 'county',
      label: 'County',
      value: listing.property.county,
    },
  ]

  if (listing.offMarketDate) {
    if (!transactionMetaLabelValues.find((v) => v.key === 'soldDate')) {
      metaLabelValues.push({
        key: 'offMarketDate',
        label: 'Transacted',
        value: new Date(listing.offMarketDate).toISOString().split('T')[0],
      })
    }
  } else if (listing.availableDate) {
    metaLabelValues.push({
      key: 'availableDate',
      label: 'Available',
      value: new Date(listing.availableDate).toISOString().split('T')[0],
    })
  }

  metaLabelValues.push(
    ...[
      {
        key: 'created',
        label: 'Created',
        value: new Date(listing.created).toISOString().split('T')[0],
      },
      {
        key: 'modified',
        label: 'Updated',
        value: new Date(listing.modified).toISOString().split('T')[0],
      },
    ]
  )
  metaLabelValues.push({
    key: 'tid',
    label: 'Listing ID',
    value: listing.tid,
  })
  if (listing.property.parcelApn) {
    metaLabelValues.push({
      key: 'parcelApn',
      label: 'Parcel APN',
      value: listing.property.parcelApn,
    })
  }

  if (listing.status === 'sold' || listing.status === 'leased') {
    const leaseRateIndex = metaLabelValues.findIndex(
      (v) => v.label === 'Lease Rate'
    )
    if (leaseRateIndex !== -1) {
      metaLabelValues[leaseRateIndex].label = 'Listed Rate'
    }
    const availableSfIndex = metaLabelValues.findIndex(
      (v) => v.label === 'Available SF'
    )
    if (availableSfIndex !== -1) {
      metaLabelValues[availableSfIndex].label = 'Listed SF'
    }
    const salePriceIndex = metaLabelValues.findIndex(
      (v) => v.label === 'Sale Price'
    )
    if (salePriceIndex !== -1) {
      metaLabelValues[salePriceIndex].label = 'Listed Price'
    }
    const salePricePerSfIndex = metaLabelValues.findIndex(
      (v) => v.label === 'Price Per SF'
    )
    if (salePricePerSfIndex !== -1) {
      metaLabelValues[salePricePerSfIndex].label = 'Listed Price Per SF'
    }
  }

  return metaLabelValues
}

function renderMinMaxFilter(
  min?: number,
  max?: number,
  prefix?: string,
  suffix?: string
) {
  if (min && max) {
    return `${prefix || ''}${fmtNumberAbbr(min)} - ${fmtNumberAbbr(max)}${
      suffix ? ` ${suffix}` : ''
    }`
  } else if (min) {
    return (
      `> ${prefix || ''} ${fmtNumberAbbr(min)}` + (suffix ? ` ${suffix}` : '')
    )
  }
  return (
    `< ${prefix || ''} ${fmtNumberAbbr(max)}` + (suffix ? ` ${suffix}` : '')
  )
}

export function getMapFilterLabels(filters: MapFiltersType) {
  const labels: string[] = []
  if (!filters) {
    return []
  }

  if (filters.search) {
    labels.push(`"${filters.search}"`)
  }

  if (filters.addressSearch) {
    labels.push(`Address: "${filters.addressSearch}"`)
  }

  if (filters.types)
    labels.push(
      filters.types
        .filter((t) => t != 'saleLease')
        .map((t) => TYPES[t])
        .join(', ')
    )
  if (filters.statuses)
    labels.push(filters.statuses.map((s) => STATUSES[s]).join(', '))
  if (filters.propertyTypes) labels.push(filters.propertyTypes.join(', '))
  if (filters.propertySubtypes) labels.push(filters.propertySubtypes.join(', '))
  if (filters.submarkets) labels.push(filters.submarkets.join(', '))
  if (filters.zoning) labels.push(filters.zoning.join(', '))
  if (filters.postalCodes)
    labels.push(
      filters.postalCodes.length < 4
        ? filters.postalCodes.join(', ')
        : `${filters.postalCodes.length} Postal Codes`
    )
  if (filters.cities)
    labels.push(
      filters.cities.length < 4
        ? filters.cities.join(', ')
        : `${filters.cities.length} Cities`
    )
  if (filters.counties)
    labels.push(
      filters.counties.length < 4
        ? filters.counties.join(', ')
        : `${filters.counties.length} Counties`
    )
  if (filters.companies)
    labels.push(
      `${filters.companies.length} ${filters.companies.length > 1 ? 'Companies' : 'Company'}`
    )
  if (filters.brokers)
    labels.push(
      `${filters.brokers.length} ${filters.brokers.length > 1 ? 'Brokers' : 'Broker'}`
    )
  if (filters.created) {
    if (filters.created.filter((v) => v).length > 1) {
      labels.push(
        `Created: ${filters.created[0]!.toString().split('T')[0]} - ${filters.created[1]!.toString().split('T')[0]}`
      )
    } else if (filters.created[0]) {
      labels.push(`Created > ${filters.created[0].toString().split('T')[0]}`)
    } else if (filters.created[1]) {
      labels.push(`Created < ${filters.created[1].toString().split('T')[0]}`)
    }
  }
  if (filters.modified) {
    if (filters.modified.filter((v) => v).length > 1) {
      labels.push(
        `Modified: ${filters.modified[0]!.toString().split('T')[0]} - ${filters.modified[1]!.toString().split('T')[0]}`
      )
    } else if (filters.modified[0]) {
      labels.push(`Modified > ${filters.modified[0].toString().split('T')[0]}`)
    } else if (filters.modified[1]) {
      labels.push(`Modified < ${filters.modified[1].toString().split('T')[0]}`)
    }
  }
  if (filters.transacted) {
    if (filters.transacted.filter((v) => v).length > 1) {
      labels.push(
        `Transacted: ${filters.transacted[0]!.toString().split('T')[0]} - ${filters.transacted[1]!.toString().split('T')[0]}`
      )
    } else if (filters.transacted[0]) {
      labels.push(
        `Transacted > ${filters.transacted[0].toString().split('T')[0]}`
      )
    } else if (filters.transacted[1]) {
      labels.push(
        `Transacted < ${filters.transacted[1].toString().split('T')[0]}`
      )
    }
  }
  if (filters.sf && filters.sf.filter((v) => v).length > 0) {
    labels.push(renderMinMaxFilter(filters.sf[0], filters.sf[1], '', 'SF'))
  }
  if (filters.acres && filters.acres.filter((v) => v).length > 0) {
    labels.push(
      renderMinMaxFilter(filters.acres[0], filters.acres[1], '', 'ac')
    )
  }
  if (filters.prices && filters.prices.filter((v) => v).length > 0) {
    labels.push(renderMinMaxFilter(filters.prices[0], filters.prices[1], '$'))
  }
  if (filters.rates && filters.rates.filter((v) => v).length > 0) {
    labels.push(
      renderMinMaxFilter(filters.rates[0], filters.rates[1], '$', 'SF/year')!
    )
  }
  if (filters.totalUnits && filters.totalUnits.filter((v) => v).length > 0) {
    labels.push(
      renderMinMaxFilter(
        filters.totalUnits[0],
        filters.totalUnits[1],
        '',
        'Units'
      )
    )
  }
  // sort
  return labels
}

export function getDataLayerEntryLabelValues(
  entry: DataLayerEntry,
  entryFields: DataLayerEntryField[]
) {
  return Object.keys(entry.meta).map((key) => {
    const field = entryFields.find((f) => f.name === key)
    return field
      ? {
          key,
          label: field.label,
          value: entry.meta[key],
          field,
        }
      : {
          key,
          label: key,
          value: entry.meta[key],
        }
  })
}

export function getTransactionTableData(
  transactions: ListingTransaction[],
  type: 'sale' | 'lease',
  {
    listing,
    filters,
    allData = false,
    excludeFields = [],
  }: {
    listing?: Listing
    filters?: MapFiltersType | undefined
    allData?: boolean
    excludeFields?: string[]
  } = {}
) {
  const filteredTransactions = getFilteredTransactions(filters, transactions)
  const rows: { [key: string]: any } = {}
  const headers: { key: string; label: string }[] = []
  if (type === 'lease') {
    filteredTransactions.forEach((transaction) => {
      // Add fields
      const space = listing?.leaseSpaces?.find(
        (s) => s._id === transaction.lease?.space?._id
      )
      const footage = transaction.lease?.footage || space?.minAvailableSf
      const acres = transaction.lease?.acres
        ? transaction.lease.acres
        : listing?.propertyTypes[0] === 'Land'
          ? listing?.meta.acres
          : undefined
      rows[transaction._id] = {
        name: transaction.lease?.space.name || 'No Name',
        date: transaction.date.split('T')[0],
        confidential: transaction.confidential ? 'Yes' : 'No',
        propertyType: transaction.propertyType || '',
        footage: footage ? `${footage.toLocaleString()} SF` : '',
        acres: acres ? `${acres.toLocaleString()} ac` : '',
      }
      if (!transaction.confidential || allData) {
        let rate = transaction.lease?.rate
          ? `$${transaction.lease?.rate.toLocaleString()}`
          : ''
        if (rate && transaction.lease?.rateType) {
          rate += ` ${RATE_TYPES[transaction.lease?.rateType]}`
        }
        rows[transaction._id] = {
          ...rows[transaction._id],
          rate,
          totalLeaseValue: transaction.lease?.totalLeaseValue
            ? `$${transaction.lease?.totalLeaseValue.toLocaleString()}`
            : '',
          structure: transaction.lease?.structure,
          termInMonths: transaction.lease?.termInMonths
            ? `${transaction.lease?.termInMonths} mo.`
            : '',
          startDate: transaction.lease?.startDate
            ? transaction.lease?.startDate.split('T')[0]
            : '',
        }
      }
    })
    const leaseHeaders = [
      { key: 'name', label: 'Name' },
      { key: 'date', label: 'Date' },
      { key: 'propertyType', label: 'Property Type' },
      { key: 'rate', label: 'Rate', confidential: true },
      { key: 'endRate', label: 'End Rate', confidential: true },
      { key: 'totalLeaseValue', label: 'Total Value', confidential: true },
      { key: 'structure', label: 'Lease Type', confidential: true },
      { key: 'footage', label: 'Footage' },
      { key: 'acres', label: 'Acres' },
      { key: 'termInMonths', label: 'Term', confidential: true },
      { key: 'startDate', label: 'Start Date', confidential: true },
      { key: 'expirationDate', label: 'Expiration Date', confidential: true },
      { key: 'confidential', label: 'Confidential' },
    ]
    leaseHeaders.forEach((header) => {
      if (
        Object.values(rows).some((row) => row[header.key]) &&
        !excludeFields.includes(header.key)
      ) {
        headers.push(header)
      }
    })
  } else {
    // sale
    filteredTransactions.forEach((transaction) => {
      const buildingSf =
        transaction.sale?.buildingSf || listing?.meta.buildingSf
      const acres =
        transaction.sale?.acres ||
        (listing?.propertyTypes[0] === 'Land' ? listing?.meta.acres : undefined)
      rows[transaction._id] = {
        date: transaction.date.split('T')[0],
        confidential: transaction.confidential ? 'Yes' : 'No',
        propertyType: transaction.propertyType || '',
        buildingSf: buildingSf ? `${buildingSf.toLocaleString()} SF` : '',
        acres: acres ? `${acres.toLocaleString()} ac` : '',
      }
      if (!transaction.confidential || allData) {
        rows[transaction._id] = {
          ...rows[transaction._id],
          price: transaction.sale?.price
            ? `$${transaction.sale?.price.toLocaleString()}`
            : '',
          terms: transaction.sale?.terms,
          // buyerName: transaction.sale?.buyerName,
          // sellerName: transaction.sale?.sellerName,
        }
      }
    })
    const saleHeaders = [
      { key: 'date', label: 'Date' },
      { key: 'propertyType', label: 'Property Type' },
      { key: 'price', label: 'Price', confidential: true },
      { key: 'buildingSf', label: 'Building SF' },
      { key: 'acres', label: 'Acres' },
      // { key: 'terms', label: 'Terms', confidential: true },
      // { key: 'buyerName', label: 'Buyer Name', confidential: true },
      // { key: 'sellerName', label: 'Seller Name', confidential: true },
      { key: 'confidential', label: 'Confidential' },
    ]
    saleHeaders.forEach((header) => {
      if (
        Object.values(rows).some((row) => row[header.key]) &&
        !excludeFields.includes(header.key)
      ) {
        headers.push(header)
      }
    })
  }

  return { headers, rows }
}

export function getHeightValue(value?: number) {
  const feet = value ? Math.floor(value) : undefined
  const inches = value
    ? Math.round((value - Math.floor(value)) * 12)
    : undefined

  return [feet && `${feet}'`, inches && `${inches}"`].filter((v) => v).join(' ')
}

const defaultCsvFields = [
  'ID',
  'Listing Name',
  'Type',
  'Status',
  'Created',
  'Modified',
  'Transacted',
  'Primary Uses',
  'Secondary Use',
  'Address',
  'Address 2',
  'City',
  'State',
  'Zip',
  'County',
  'Latitude',
  'Longitude',
  'Parcel APN',
  'Company',
  'Broker Name',
  'Broker Email',
  'Broker Phone',
  'Lease Rate',
  'Available SF',
]

export function fmtListingsToCSV(
  listings: (Listing & { brokers?: ListingBroker[]; company?: ModelCompany })[],
  separateSpaces = false,
  filters?: MapFiltersType // For filtering lease spaces by dates
): {
  data: CSVRow[]
  fields: string[]
} {
  const metaFieldsWithData: Record<string, boolean> = {}
  const data: CSVRow[] = []

  // Modify fields based on separateSpaces parameter
  let fields = [...defaultCsvFields]
  if (separateSpaces) {
    // Remove 'Lease Rate' and 'Available SF' from fields
    fields = fields.filter(
      (field) => field !== 'Lease Rate' && field !== 'Available SF'
    )
    // Add space-specific fields after 'Status'
    const statusIndex = fields.indexOf('Status')
    fields.splice(statusIndex + 1, 0, 'Space ID', 'Space Name', 'Space Floor')
    // Add space-specific fields at the end
    fields.push(
      'Min Rate',
      'Max Rate',
      'Rate Type',
      'Min Available SF',
      'Max Available SF',
      'Space Acres',
      'Sublease',
      'Available',
      'Tenant Name',
      'Description',
      'Former Use'
    )
  }

  for (const listing of listings) {
    const row: CSVRow = {
      ID: listing.tid,
      'Listing Name': listing.name,
      Type: TYPES[listing.type],
      Status: STATUSES[listing.status],
      Created: listing.created,
      Modified: listing.modified,
      Transacted: listing.offMarketDate ? listing.offMarketDate : '',
      'Primary Uses': listing.propertyTypes.join(', '),
      'Secondary Use': (listing.propertySubtypes || []).join(', '),
      Address: listing.property?.address,
      'Address 2': listing.property?.address2,
      City: listing.property?.city,
      State: listing.property?.stateAbbr,
      Zip: listing.property?.postalCode,
      County: listing.property?.county,
      Latitude: listing.property?.latitude.toString(),
      Longitude: listing.property?.longitude.toString(),
      'Parcel APN': listing.property?.parcelApn,
      Company: listing.company?.name,
      'Broker Name':
        listing.brokers?.length > 0
          ? fmtName(
              listing.brokers[0].firstName,
              listing.brokers[0].middleName,
              listing.brokers[0].lastName
            )
          : '',
      'Broker Email':
        listing.brokers?.length > 0 ? listing.brokers[0].email : '',
      'Broker Phone':
        listing.brokers?.length > 0 ? listing.brokers[0].meta?.office : '',
    }
    if (listing.isBusinessListing) {
      if (listing.locationVisibility === 'state') {
        row.City = undefined
        row.Zip = undefined
        row.County = undefined
      } else if (listing.locationVisibility === 'county') {
        row.City = undefined
      }
    }

    if (separateSpaces === false) {
      getLeaseMetaValues(listing, false, true).forEach(({ label, value }) => {
        // TODO renaming labels until we change
        if (label === 'Listed Rate') label = 'Lease Rate'
        if (label === 'Listed SF') label = 'Available SF'
        row[label] = value
        metaFieldsWithData[label] = true
      })
    }

    getMetaLabelValues(listing, true).forEach(({ label, value }) => {
      row[label] = value
      metaFieldsWithData[label] = true
    })

    if (separateSpaces === false || (listing.leaseSpaces || []).length === 0) {
      data.push(row)
    } else {
      const { matches: filteredLeaseSpaces } = getFilteredLeaseSpaces(
        filters,
        listing.leaseSpaces || [],
        listing.propertyTypes[0]
      )
      for (const space of filteredLeaseSpaces) {
        const spaceRow = { ...row }
        spaceRow['Created'] = space.created
        spaceRow['Modified'] = space.modified
        const spaceTransaction = (listing.transactions || []).find(
          (t) => t.lease?.space._id === space._id
        )
        if (spaceTransaction) {
          spaceRow['Transacted'] = spaceTransaction.date
        }

        const spaceData: Record<string, string | undefined> = {
          'Space ID': space._id.toString().split('-')[0],
          'Space Name': space.name,
          'Space Floor': space.floor,
          'Min Rate': space.rateNegotiable
            ? 'Negotiable'
            : space.minRate?.toString(),
          'Max Rate': space.rateNegotiable
            ? 'Negotiable'
            : space.maxRate?.toString(),
          'Rate Type': space.rateType,
          'Min Available SF': space.minAvailableSf?.toString(),
          'Max Available SF': space.maxAvailableSf?.toString(),
          'Space Acres': space.acres?.toString(),
          Sublease: space.sublease ? 'Yes' : 'No',
          Available: space.available ? 'Yes' : 'No',
          'Tenant Name': space.tenantName,
          Description: space.description,
          'Former Use': space.formerUse,
        }

        data.push({ ...spaceRow, ...spaceData })
      }
    }
  }

  const finalFields = [...fields]
  Object.keys(metaFieldsWithData).forEach((key) => {
    if (metaFieldsWithData[key]) {
      finalFields.push(key)
    }
  })

  return { data, fields: finalFields }
}

export function sortLeaseSpaces(
  leaseSpaces: Listing['leaseSpaces'],
  transactions: Listing['transactions'],
  leaseSpaceSorting?: 'created' | 'numeric'
) {
  if (!leaseSpaces || leaseSpaces.length === 0) {
    return []
  }
  const spaces = [...(leaseSpaces ?? [])]

  const spaceTransactionMap = new Map<string, boolean>()
  transactions?.forEach((transaction) => {
    if (transaction.lease?.space?._id) {
      spaceTransactionMap.set(transaction.lease.space._id, true)
    }
  })
  spaces.sort((a, b) => {
    const aHasTransaction = spaceTransactionMap.has(a._id)
    const bHasTransaction = spaceTransactionMap.has(b._id)

    if (aHasTransaction !== bHasTransaction) {
      return aHasTransaction ? 1 : -1
    }

    if (leaseSpaceSorting === 'numeric') {
      const aParts = (a.name || '')
        .split(/[\s\-_|\.]+/)
        .filter((part) => part.length > 0)
      const bParts = (b.name || '')
        .split(/[\s\-_|\.]+/)
        .filter((part) => part.length > 0)

      const maxParts = Math.max(aParts.length, bParts.length)

      for (let i = 0; i < maxParts; i++) {
        const aPart = aParts[i] || ''
        const bPart = bParts[i] || ''

        // If one part is empty and the other isn't, the one with fewer parts comes first
        if (!aPart && bPart) {
          return -1
        }
        if (aPart && !bPart) {
          return 1
        }
        if (!aPart && !bPart) {
          continue
        }

        const aMatch = aPart.match(/^(\d+)(.*)$/)
        const bMatch = bPart.match(/^(\d+)(.*)$/)

        if (aMatch && bMatch) {
          const aNum = parseInt(aMatch[1], 10)
          const bNum = parseInt(bMatch[1], 10)
          if (aNum !== bNum) {
            return aNum - bNum
          }
          if (aMatch[2] !== bMatch[2]) {
            return aMatch[2].localeCompare(bMatch[2])
          }
        } else if (aMatch && !bMatch) {
          return -1
        } else if (!aMatch && bMatch) {
          return 1
        } else {
          const partComparison = aPart.localeCompare(bPart)
          if (partComparison !== 0) {
            return partComparison
          }
        }
      }

      return 0
    }
    return new Date(b.created).getTime() - new Date(a.created).getTime()
  })
  return spaces
}

export function getNormalizedSF(sf?: number, acres?: number) {
  if (sf !== undefined) return sf

  if (acres !== undefined) {
    return acres * SF_IN_ACRES
  }

  return 0
}

export function getNormalizedRate(
  rate: number,
  rateType?: string,
  sf?: number,
  acres?: number
) {
  if (!rateType) {
    return undefined
  } else if (rateType === 'sfMonth') {
    return Math.round(rate * 12 * 100) / 100
  } else if (rateType === 'sfYear') {
    return rate
  }

  // Acre-based rate types
  if (acres) {
    if (rateType === 'acMonth') {
      return (
        Math.round(((rate * acres * 12) / (acres * SF_IN_ACRES)) * 100) / 100
      )
    } else if (rateType === 'acYear') {
      return Math.round((rate / (acres * SF_IN_ACRES)) * 100) / 100
    } else if (rateType === 'month') {
      return Math.round(((rate * 12) / (acres * SF_IN_ACRES)) * 100) / 100
    } else if (rateType === 'year') {
      return Math.round((rate / (acres * SF_IN_ACRES)) * 100) / 100
    }
    return undefined
  }

  if (sf) {
    if (rateType === 'month') {
      return Math.round(((rate * 12) / sf) * 100) / 100
    } else if (rateType === 'year') {
      return Math.round((rate / sf) * 100) / 100
    }
  }

  return undefined
}

export function getFilteredTransactions(
  filters: MapFiltersType | undefined,
  transactions: ListingTransaction[]
) {
  if (filters && filters.transacted && filters.transacted.length > 0) {
    const startDate = filters.transacted[0]
      ? new Date(filters.transacted![0]!)
      : null
    const endDate = filters.transacted[1]
      ? new Date(filters.transacted![1]!)
      : null
    return (
      transactions.filter((transaction) => {
        const transactionDate = new Date(transaction.date)
        if (startDate && endDate) {
          return transactionDate >= startDate && transactionDate <= endDate
        } else if (startDate) {
          return transactionDate >= startDate
        } else if (endDate) {
          return transactionDate <= endDate
        }
      }) || []
    )
  }
  return transactions
}

export function getFilteredLeaseSpaces(
  filters: MapFiltersType | undefined,
  leaseSpaces: ListingLeaseSpace[],
  primaryPropertyType: string
) {
  let filteredLeaseSpaces = [...leaseSpaces]
  if (filters) {
    if (filters.created && filters.created.length > 0) {
      const startDate = filters.created[0]
        ? new Date(filters.created![0]!)
        : null
      const endDate = filters.created[1] ? new Date(filters.created![1]!) : null
      filteredLeaseSpaces = filteredLeaseSpaces.filter((space) => {
        const spaceDate = new Date(space.created)
        if (startDate && endDate) {
          return spaceDate >= startDate && spaceDate <= endDate
        } else if (startDate) {
          return spaceDate >= startDate
        } else if (endDate) {
          return spaceDate <= endDate
        }
        return true
      })
    }
    if (filters.modified && filters.modified.length > 0) {
      const startDate = filters.modified[0]
        ? new Date(filters.modified![0]!)
        : null
      const endDate = filters.modified[1]
        ? new Date(filters.modified![1]!)
        : null
      filteredLeaseSpaces = filteredLeaseSpaces.filter((space) => {
        const spaceDate = new Date(space.modified)
        if (startDate && endDate) {
          return spaceDate >= startDate && spaceDate <= endDate
        } else if (startDate) {
          return spaceDate >= startDate
        } else if (endDate) {
          return spaceDate <= endDate
        }
        return true
      })
    }
    if (filters.sf && filters.sf.length > 0) {
      const minSf = filters.sf[0] || null
      const maxSf = filters.sf[1] || null
      filteredLeaseSpaces = filteredLeaseSpaces.filter((space) => {
        const spaceMinSf = space.minAvailableSf || 0
        const spaceMaxSf = space.maxAvailableSf || 0
        if (minSf && maxSf) {
          return spaceMaxSf >= minSf && spaceMinSf <= maxSf
        } else if (minSf) {
          return spaceMaxSf >= minSf
        } else if (maxSf) {
          return spaceMinSf <= maxSf
        }
        return true
      })
    }
    if (filters.rates && filters.rates.length > 0) {
      const minRate = filters.rates[0] || null
      const maxRate = filters.rates[1] || null
      filteredLeaseSpaces = filteredLeaseSpaces.filter((space) => {
        const spaceMinRate =
          getNormalizedRate(
            space.minRate || 0,
            space.rateType,
            space.minAvailableSf,
            space.acres
          ) || 0
        const spaceMaxRate =
          getNormalizedRate(
            space.maxRate || 0,
            space.rateType,
            space.maxAvailableSf,
            space.acres
          ) || 0
        if (minRate && maxRate) {
          return spaceMaxRate >= minRate && spaceMinRate <= maxRate
        } else if (minRate) {
          return spaceMaxRate >= minRate
        } else if (maxRate) {
          return spaceMinRate <= maxRate
        }
        return true
      })
    }
    if (
      primaryPropertyType === 'Land' &&
      filters.acres &&
      filters.acres.length > 0
    ) {
      const minAcres = filters.acres[0] || null
      const maxAcres = filters.acres[1] || null
      filteredLeaseSpaces = filteredLeaseSpaces.filter((space) => {
        const spaceAcres = space.acres || 0
        if (minAcres && maxAcres) {
          return spaceAcres >= minAcres && spaceAcres <= maxAcres
        } else if (minAcres) {
          return spaceAcres >= minAcres
        } else if (maxAcres) {
          return spaceAcres <= maxAcres
        }
        return true
      })
    }
  }
  const nonMatches = leaseSpaces.filter(
    (space) => !filteredLeaseSpaces.includes(space)
  )
  return { matches: filteredLeaseSpaces, nonMatches }
}
